/**
 * scan-graph.js
 * Generic animated SVG line chart custom element.
 *
 * Supported data attributes:
 *   data-line1 / line2 / line3 / line4   JSON number arrays (all same length)
 *   data-line1-color … line4-color        Hex fill colors
 *   data-line1-label … line4-label        Legend strings
 *   data-y-max                            Y-axis maximum
 *   data-y-step                           Optional Y gridline interval (overrides auto)
 *   data-y-label                          Y-axis title
 *   data-x-label                          X-axis caption (numeric mode only)
 *   data-x-labels                         JSON string array for custom X tick labels
 *   data-x-grid                           "true" → draw vertical gridlines
 *   data-stat-value                       Big callout number, e.g. "653%"
 *   data-stat-text                        Callout subtitle line 1 (normal weight)
 *   data-stat-line2                       Callout subtitle line 2 (bold weight)
 *
 * ViewBox: 0 0 700 450
 * Plot area: left=60, top=56, right=680, bottom=400
 * Legend row 1: y=16  (primary series)
 * Legend row 2: y=36  (secondary/tertiary/quaternary, when 3+ series)
 */

class ScanGraph extends HTMLElement {
  connectedCallback() {
    this.d1    = JSON.parse(this.dataset.line1    || '[]');
    this.d2    = JSON.parse(this.dataset.line2    || '[]');
    this.d3    = this.dataset.line3 ? JSON.parse(this.dataset.line3) : null;
    this.d4    = this.dataset.line4 ? JSON.parse(this.dataset.line4) : null;
    this.c1    = this.dataset.line1Color          || '#2196F3';
    this.c2    = this.dataset.line2Color          || '#aaaaaa';
    this.c3    = this.dataset.line3Color          || '#888888';
    this.c4    = this.dataset.line4Color          || '#bbbbbb';
    this.lbl1  = this.dataset.line1Label          || '';
    this.lbl2  = this.dataset.line2Label          || '';
    this.lbl3  = this.dataset.line3Label          || '';
    this.lbl4  = this.dataset.line4Label          || '';
    this.yMax  = parseInt(this.dataset.yMax, 10)  || 500;
    this.yStep = this.dataset.yStep ? parseInt(this.dataset.yStep, 10) : null;
    this.yLbl  = this.dataset.yLabel              || '';
    this.xLbl  = this.dataset.xLabel              || '';
    this.xLbls = this.dataset.xLabels ? JSON.parse(this.dataset.xLabels) : null;
    this.xGrid = this.dataset.xGrid === 'true';
    this.statV = this.dataset.statValue           || '';
    this.statT = this.dataset.statText            || '';
    this.statL2 = this.dataset.statLine2          || '';

    this.svg        = this.querySelector('svg');
    this.done       = false;
    this._textScale = 1;

    if (!this.svg || !this.d1.length || !this.d2.length) return;

    this._render();

    // One-shot ResizeObserver: recalculate text scale once the SVG has laid out
    const ro = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (!w) return;
      ro.disconnect();
      const ts = Math.max(1, Math.min(1.35, 520 / w));
      if (Math.abs(ts - this._textScale) < 0.08) return;
      this._textScale = ts;
      const wasDone = this.done;
      this._render();
      if (wasDone) { this._finish(); this.done = true; }
    });
    ro.observe(this.svg);
  }

  // ─── Re-draw and re-arm observers ─────────────────────────────────────────
  _render() {
    this.done = false;
    this.svg.replaceChildren();
    this._draw();
    if (this.closest('swiper-slide')?.classList.contains('swiper-slide-duplicate')) {
      this._finish();
    } else {
      this._observe();
    }
  }

  // ─── Coordinate helpers (ViewBox 0 0 700 450, plot l=60 t=56 r=680 b=400) ──
  _cx(i) { return 60 + (i / (this.d1.length - 1)) * 620; }
  _cy(v) { return 400 - (v / this.yMax) * 344; }
  _pts(d) { return d.map((v, i) => `${this._cx(i).toFixed(1)},${this._cy(v).toFixed(1)}`).join(' '); }

  // ─── SVG element helpers ───────────────────────────────────────────────────
  _el(tag, attrs) {
    const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
    return el;
  }

  _txt(content, attrs) {
    const el = this._el('text', attrs);
    el.textContent = content;
    return el;
  }

  // "Nice" Y gridline step — uses data-y-step if provided, otherwise auto
  _gridStep() {
    if (this.yStep) return this.yStep;
    const step = Math.ceil(this.yMax / 5 / 100) * 100;
    return step || 100;
  }

  // ─── Draw all SVG elements ─────────────────────────────────────────────────
  _draw() {
    const s    = this.svg;
    const n    = this.d1.length - 1;
    const font = 'system-ui,sans-serif';
    const multiSeries = !!this.d3; // 3+ series → two-row legend

    // Text scale derived from ResizeObserver measurement
    const ts = this._textScale;
    const fs = Math.round(14 * ts);   // standard font size
    const r1 = Math.round(9  * ts);   // primary dot radius
    const r2 = Math.round(5  * ts);   // secondary dot radius

    // Expand viewBox to give x-axis labels more headroom
    s.setAttribute('viewBox', '0 0 700 462');

    // ── Legend ───────────────────────────────────────────────────────────────
    if (multiSeries) {
      // Row 1: primary series (large dot)
      s.append(this._el('circle', { cx: 60, cy: 16, r: r1, fill: this.c1 }));
      s.append(this._txt(this.lbl1, {
        x: 60 + r1 + 9, y: 16, 'dominant-baseline': 'middle',
        'font-size': fs, 'font-weight': 600, fill: '#374151', 'font-family': font,
      }));
      // Row 2: comparison series (small dots)
      const row2 = [
        { cx: 60,  color: this.c2, lbl: this.lbl2 },
        { cx: 260, color: this.c3, lbl: this.lbl3 },
        ...(this.d4 ? [{ cx: 450, color: this.c4, lbl: this.lbl4 }] : []),
      ];
      row2.forEach(({ cx, color, lbl }) => {
        s.append(this._el('circle', { cx, cy: 36, r: r2, fill: color }));
        s.append(this._txt(lbl, {
          x: cx + r2 + 8, y: 36, 'dominant-baseline': 'middle',
          'font-size': fs, fill: '#6b7280', 'font-family': font,
        }));
      });
    } else {
      // Single row: primary + one secondary
      s.append(this._el('circle', { cx: 60, cy: 22, r: r1, fill: this.c1 }));
      s.append(this._txt(this.lbl1, {
        x: 60 + r1 + 9, y: 22, 'dominant-baseline': 'middle',
        'font-size': fs, 'font-weight': 600, fill: '#374151', 'font-family': font,
      }));
      s.append(this._el('circle', { cx: 290, cy: 22, r: r2, fill: this.c2 }));
      s.append(this._txt(this.lbl2, {
        x: 290 + r2 + 8, y: 22, 'dominant-baseline': 'middle',
        'font-size': fs, fill: '#6b7280', 'font-family': font,
      }));
    }

    // ── Y-axis title (rotated) ────────────────────────────────────────────────
    const yMid = (56 + 400) / 2;
    s.append(this._txt(this.yLbl.toUpperCase(), {
      x: 12, y: yMid, 'text-anchor': 'middle',
      transform: `rotate(-90,12,${yMid})`,
      'font-size': fs, fill: '#9ca3af', 'letter-spacing': 1.5, 'font-family': font,
    }));

    // ── Horizontal grid lines + Y labels ─────────────────────────────────────
    const step = this._gridStep();
    for (let v = 0; v <= this.yMax; v += step) {
      const yp = this._cy(v);
      s.append(this._el('line', { x1: 60, y1: yp, x2: 680, y2: yp, stroke: '#e5e7eb', 'stroke-width': 1 }));
      s.append(this._txt(v, {
        x: 52, y: yp, 'text-anchor': 'end', 'dominant-baseline': 'middle',
        'font-size': fs, 'font-weight': 600, fill: '#6b7280', 'font-family': font,
      }));
    }

    // ── Vertical grid lines (optional) ───────────────────────────────────────
    if (this.xGrid) {
      for (let i = 0; i <= n; i++) {
        s.append(this._el('line', {
          x1: this._cx(i).toFixed(1), y1: 56,
          x2: this._cx(i).toFixed(1), y2: 400,
          stroke: '#e5e7eb', 'stroke-width': 1,
        }));
      }
    }

    // ── X labels ─────────────────────────────────────────────────────────────
    if (this.xLbls) {
      this.xLbls.forEach((lbl, i) => {
        if (!lbl) return;
        s.append(this._txt(lbl, {
          x: this._cx(i).toFixed(1), y: 430, 'text-anchor': 'middle',
          'font-size': fs, fill: '#9ca3af', 'letter-spacing': 1.5, 'font-family': font,
        }));
      });
    } else {
      for (let i = 1; i <= n; i++) {
        s.append(this._txt(i, {
          x: this._cx(i).toFixed(1), y: 430, 'text-anchor': 'middle',
          'font-size': fs, 'font-weight': 600, fill: '#6b7280', 'font-family': font,
        }));
      }
    }
    if (this.xLbl) {
      s.append(this._txt(this.xLbl.toUpperCase(), {
        x: 60, y: 452, 'text-anchor': 'start',
        'font-size': fs, fill: '#9ca3af', 'letter-spacing': 1.5, 'font-family': font,
      }));
    }

    // ── Extra comparison lines (drawn bottom-up so primary is on top) ─────────
    [
      { data: this.d4, color: this.c4 },
      { data: this.d3, color: this.c3 },
    ].forEach(({ data, color }) => {
      if (!data) return;
      s.append(this._el('polyline', {
        points: this._pts(data),
        fill: 'none', stroke: color, 'stroke-width': 2,
        'stroke-dasharray': '7 5', 'stroke-linecap': 'round',
      }));
      data.forEach((v, i) => {
        s.append(this._el('circle', {
          cx: this._cx(i).toFixed(1), cy: this._cy(v).toFixed(1),
          r: r2, fill: color, stroke: 'white', 'stroke-width': 1.5,
        }));
      });
    });

    // ── Secondary line (dashed, static) ──────────────────────────────────────
    s.append(this._el('polyline', {
      points: this._pts(this.d2),
      fill: 'none', stroke: this.c2, 'stroke-width': 2,
      'stroke-dasharray': '7 5', 'stroke-linecap': 'round',
    }));
    this._dots2 = this.d2.map((v, i) => {
      const c = this._el('circle', {
        cx: this._cx(i).toFixed(1), cy: this._cy(v).toFixed(1),
        r: r2, fill: this.c2, stroke: 'white', 'stroke-width': 1.5,
      });
      s.append(c);
      return c;
    });

    // ── Primary line — starts at d2 positions, animates to d1 ────────────────
    this._poly1 = this._el('polyline', {
      points: this._pts(this.d2),
      fill: 'none', stroke: this.c1, 'stroke-width': 3,
      'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    });
    s.append(this._poly1);

    this._dots1 = this.d1.map((_, i) => {
      const c = this._el('circle', {
        cx: this._cx(i).toFixed(1),
        cy: this._cy(this.d2[i] ?? 0).toFixed(1),
        r: r1, fill: this.c1, stroke: 'white', 'stroke-width': 2.5,
      });
      s.append(c);
      return c;
    });

    // ── Stat callout box (optional, always on top) ────────────────────────────
    if (this.statV) this._drawStat(font);
  }

  // ─── Stat callout (top-right of chart area) ────────────────────────────────
  _drawStat(font) {
    const s = this.svg;
    const hasLine2 = !!this.statL2;
    const bx = 505, by = 56, bw = 175, bh = hasLine2 ? 116 : 100;

    s.append(this._el('rect', {
      x: bx, y: by, width: bw, height: bh, rx: 12,
      fill: 'white', stroke: '#e5e7eb', 'stroke-width': 1.5,
    }));

    // Big number
    s.append(this._txt(this.statV, {
      x: bx + 14, y: by + 62,
      'font-size': 48, 'font-weight': 900, fill: '#111827', 'font-family': font,
    }));

    // Trend icon
    const ix = bx + bw - 38, iy = by + 14;
    s.append(this._el('polyline', {
      points: `${ix},${iy + 26} ${ix + 10},${iy + 14} ${ix + 18},${iy + 18} ${ix + 28},${iy}`,
      fill: 'none', stroke: this.c1, 'stroke-width': 2,
      'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    }));
    s.append(this._el('circle', { cx: ix + 28, cy: iy, r: 2.5, fill: this.c1 }));

    // Subtitle line 1
    if (this.statT) {
      s.append(this._txt(this.statT, {
        x: bx + 14, y: by + (hasLine2 ? 82 : 84),
        'font-size': 12, fill: '#374151', 'font-family': font,
      }));
    }

    // Subtitle line 2 (bold)
    if (hasLine2) {
      s.append(this._txt(this.statL2, {
        x: bx + 14, y: by + 98,
        'font-size': 12, 'font-weight': 700, fill: '#374151', 'font-family': font,
      }));
    }
  }

  // ─── Intersection observer ─────────────────────────────────────────────────
  _observe() {
    const target = this.closest('swiper-slide') || this;
    const slideIndex = parseInt(target.dataset.swiperSlideIndex ?? target.dataset.index ?? 0, 10) || 0;
    const stagger = slideIndex * 150;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || this.done) return;
      this.done = true;
      io.disconnect();
      setTimeout(() => this._animate(), 350 + stagger);
    }, { threshold: 0.9 });
    io.observe(target);
  }

  // ─── Snap primary to final positions (used by Swiper duplicate slides) ─────
  _finish() {
    this._poly1.setAttribute('points', this._pts(this.d1));
    this._dots1.forEach((dot, i) => {
      dot.setAttribute('cy', this._cy(this.d1[i]).toFixed(2));
    });
  }

  // ─── Animation: interpolate primary from secondary → actual ───────────────
  _animate() {
    const dur   = 1100;
    const start = performance.now();
    const ease  = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const tick = (now) => {
      const p = ease(Math.min((now - start) / dur, 1));

      this._poly1.setAttribute('points', this.d1.map((v, i) => {
        const y0 = this._cy(this.d2[i] ?? 0);
        const y1 = this._cy(v);
        return `${this._cx(i).toFixed(1)},${(y0 + (y1 - y0) * p).toFixed(2)}`;
      }).join(' '));

      this._dots1.forEach((dot, i) => {
        const y0 = this._cy(this.d2[i] ?? 0);
        const y1 = this._cy(this.d1[i]);
        dot.setAttribute('cy', (y0 + (y1 - y0) * p).toFixed(2));
      });

      if (p < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }
}

if (!customElements.get('scan-graph')) {
  customElements.define('scan-graph', ScanGraph);
}

// =============================================================================
// ScanBar — animated horizontal arrow-bar chart
//
// Supported data attributes:
//   data-bar1 / bar2              Numeric values
//   data-bar1-color / bar2-color  Hex fill colors
//   data-bar1-label / bar2-label  Row label strings (use | for line break)
//   data-x-max                    X-axis maximum value
//   data-x-step                   X-axis gridline / tick interval
//   data-x-label                  Bottom caption
//   data-x-format                 "K" → divide by 1000 and append "K"
//
// ViewBox: 0 0 700 245
// Label column: x=0–200  |  Chart area: x=200–680 (480px)
// Strip 1: y=20–98 (cy=59)  |  Strip 2: y=102–180 (cy=141)
// =============================================================================

class ScanBar extends HTMLElement {
  connectedCallback() {
    this.v1   = parseInt(this.dataset.bar1, 10) || 0;
    this.v2   = parseInt(this.dataset.bar2, 10) || 0;
    this.c1   = this.dataset.bar1Color || '#2196F3';
    this.c2   = this.dataset.bar2Color || '#b0b0b0';
    this.lbl1 = this.dataset.bar1Label || '';
    this.lbl2 = this.dataset.bar2Label || '';
    this.xMax  = parseInt(this.dataset.xMax,  10) || 30000;
    this.xStep = parseInt(this.dataset.xStep, 10) || 10000;
    this.xLbl  = this.dataset.xLabel  || '';
    this.xFmt  = this.dataset.xFormat || '';

    this.svg        = this.querySelector('svg');
    this.done       = false;
    this._textScale = 1;

    if (!this.svg) return;

    this._render();

    const ro = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (!w) return;
      ro.disconnect();
      const ts = Math.max(1, Math.min(1.35, 520 / w));
      if (Math.abs(ts - this._textScale) < 0.08) return;
      this._textScale = ts;
      const wasDone = this.done;
      this._render();
      if (wasDone) { this._finish(); this.done = true; }
    });
    ro.observe(this.svg);
  }

  // ─── Re-draw and re-arm observers ─────────────────────────────────────────
  _render() {
    this.done = false;
    this.svg.replaceChildren();
    this._draw();
    if (this.closest('swiper-slide')?.classList.contains('swiper-slide-duplicate')) {
      this._finish();
    } else {
      this._observe();
    }
  }

  // Label column widens with text scale so text never crowds into bar area
  get _labelW() { return Math.round(200 * this._textScale); }

  _xpos(v) { return this._labelW + (v / this.xMax) * (680 - this._labelW); }

  _fmt(v) {
    if (this.xFmt === 'K') return (v / 1000) + 'K';
    return String(v);
  }

  _el(tag, attrs) {
    const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
    return el;
  }

  _txt(content, attrs) {
    const el = this._el('text', attrs);
    el.textContent = content;
    return el;
  }

  _barPath(x0, x1, cy, h, tip) {
    const top = cy - h / 2, bot = cy + h / 2;
    if (x1 - x0 < tip) {
      return `M${x0},${top} L${x1},${top} L${x1},${bot} L${x0},${bot} Z`;
    }
    return `M${x0},${top} L${(x1 - tip).toFixed(1)},${top} L${x1.toFixed(1)},${cy} L${(x1 - tip).toFixed(1)},${bot} L${x0},${bot} Z`;
  }

  _drawLabel(label, x, cy, font, fs) {
    const s = this.svg;
    const parts = label.split('|');
    if (parts.length === 1) {
      s.append(this._txt(label, {
        x, y: cy, 'dominant-baseline': 'middle',
        'font-size': fs, fill: '#374151', 'font-family': font,
      }));
    } else {
      const el = this._el('text', {
        x, y: cy - 9, 'font-size': fs, fill: '#374151', 'font-family': font,
      });
      parts.forEach((part, i) => {
        const ts = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        ts.textContent = part;
        ts.setAttribute('x', x);
        if (i > 0) ts.setAttribute('dy', '18');
        el.appendChild(ts);
      });
      s.append(el);
    }
  }

  _draw() {
    const s    = this.svg;
    const font = 'system-ui,sans-serif';
    const BAR_H = 72, TIP = 22;

    const ts = this._textScale;
    const fs = Math.round(14 * ts);

    const rows = [
      { cy: 70,  label: this.lbl1, color: this.c1, val: this.v1 },
      { cy: 182, label: this.lbl2, color: this.c2, val: this.v2 },
    ];

    s.setAttribute('viewBox', '0 -80 700 462');

    const labelW = this._labelW;
    rows.forEach(({ cy }) => {
      s.append(this._el('rect', {
        x: 0, y: cy - BAR_H / 2, width: labelW, height: BAR_H, fill: '#e7fdfc',
      }));
    });

    for (let v = 0; v <= this.xMax; v += this.xStep) {
      const x = this._xpos(v).toFixed(1);
      s.append(this._el('line', {
        x1: x, y1: 34, x2: x, y2: 218,
        stroke: '#c8dbd9', 'stroke-width': 1,
      }));
    }

    rows.forEach(({ cy, label }) => {
      this._drawLabel(label, 14, cy, font, fs);
    });

    const x0 = this._xpos(0);

    s.append(this._el('path', {
      d: this._barPath(x0, this._xpos(this.v1), rows[0].cy, BAR_H, TIP),
      fill: this.c1,
    }));

    const xStart = this._xpos(this.v1);
    const bar2el = this._el('path', {
      d: this._barPath(x0, xStart, rows[1].cy, BAR_H, TIP),
      fill: this.c2,
    });
    s.append(bar2el);
    this._bar2 = { el: bar2el, x0, xStart, x1: this._xpos(this.v2), cy: rows[1].cy };

    for (let v = 0; v <= this.xMax; v += this.xStep) {
      s.append(this._txt(this._fmt(v), {
        x: this._xpos(v).toFixed(1), y: 238, 'text-anchor': 'middle',
        'font-size': fs, 'font-weight': 600, fill: '#6b7280', 'font-family': font,
      }));
    }

    if (this.xLbl) {
      s.append(this._txt(this.xLbl.toUpperCase(), {
        x: 14, y: 262, 'text-anchor': 'start',
        'font-size': fs, fill: '#9ca3af', 'letter-spacing': 1.5, 'font-family': font,
      }));
    }
  }

  _finish() {
    const { el, x0, x1, cy } = this._bar2;
    el.setAttribute('d', this._barPath(x0, x1, cy, 72, 22));
  }

  _observe() {
    const target = this.closest('swiper-slide') || this;
    const slideIndex = parseInt(target.dataset.swiperSlideIndex ?? target.dataset.index ?? 0, 10) || 0;
    const stagger = slideIndex * 150;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || this.done) return;
      this.done = true;
      io.disconnect();
      setTimeout(() => this._animate(), 350 + stagger);
    }, { threshold: 0.9 });
    io.observe(target);
  }

  _animate() {
    const dur   = 1100;
    const start = performance.now();
    const ease  = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const { el, x0, xStart, x1, cy } = this._bar2;
    const tick = (now) => {
      const p = ease(Math.min((now - start) / dur, 1));
      el.setAttribute('d', this._barPath(x0, xStart + (x1 - xStart) * p, cy, 72, 22));
      if (p < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }
}

if (!customElements.get('scan-bar')) {
  customElements.define('scan-bar', ScanBar);
}
